import { Injectable, Injector, Type } from '@angular/core';
import { ConfirmationService } from 'primeng/api';
import { BehaviorSubject, distinctUntilChanged, map, shareReplay, Subject, switchMap, timer } from 'rxjs';
import { binaryOptions } from '../utils/enums/binary';
import _ from 'lodash';
import moment from 'moment';
import { TranslateService } from '@ngx-translate/core';
import { activeOptions } from '../utils/enums/active';
import { quoteActiveOptions } from '../utils/enums/quote-active';
import { invoiceStatusOptions } from '../utils/enums/invoice-status-options';
import { paymentMethods } from '../utils/enums/payment-methods';
import { colors } from '../utils/enums/colors';
import CryptoJS from 'crypto-js';
import { itemsModesOptions } from '../utils/enums/items-modes';
import { invoiceModesOptions } from '../utils/enums/invoice-modes';
import tippy from 'tippy.js';
import { attendanceOptions } from '../utils/enums/attendance-options';
import { quantityTypes } from '../utils/enums/quantity-types';
import { occupationTypes } from '../utils/enums/occupation-types';
import { timesheetRecordOptions } from '../utils/enums/timesheet-record-options';
import { AbstractControl, FormGroup } from '@angular/forms';
import { timesheetRecordStatusOptions } from '../utils/enums/timesheet-record-status-options';
import { privateOptions } from '../utils/enums/private-options';
import { docSignatureTypes } from '../utils/enums/doc-signature-types';
import { DocSignatureType } from '../models/document-signature-type';
import { absenceTypes } from '../utils/enums/absence-types';
import { vacationTypes } from '../utils/enums/vacation-types';
import { invoiceTypesOptions } from '../utils/enums/invoice-types';
import { customerCenters } from '../utils/enums/customer-centers';
import { NGXLogger } from 'ngx-logger';
import * as XLSX from 'xlsx';
import FileSaver from 'file-saver';

type DrawerPosition = 'left' | 'right';

interface DrawerState {
  component: Type<any> | null;
  data?: any;
  isOpen: boolean;
  response?: any;
}

@Injectable({
  providedIn: 'root'
})
export class AuxiliarService {
  private calendarSubject = new BehaviorSubject(false);
  public calendarObservable = this.calendarSubject.asObservable();
  private drawerStates: Record<DrawerPosition, BehaviorSubject<DrawerState>> = {
    left: new BehaviorSubject<DrawerState>({ isOpen: false, component: null }),
    right: new BehaviorSubject<DrawerState>({ isOpen: false, component: null }),
  };
  public drawerObservables = {
    left: this.drawerStates.left.asObservable(),
    right: this.drawerStates.right.asObservable(),
  };
  public drawerCloseSubjects: Record<DrawerPosition, Subject<any>> = {
    left: new Subject<any>(),
    right: new Subject<any>(),
  };
  private spinnerReqs = new BehaviorSubject<number>(0);
  public spinner = this.spinnerReqs.pipe(
    map(v => v > 0),
    distinctUntilChanged(),
    switchMap(visible => {
      if (visible) {
        // delay show
        return timer(150).pipe(map(() => true));
      } else {
        // delay hide
        return timer(300).pipe(map(() => false));
      }
    }),
    distinctUntilChanged(),
    shareReplay(1)
  );
  private toastSubject = new BehaviorSubject({});
  public toastObservable = this.toastSubject.asObservable();

  constructor(
    private confirmationService: ConfirmationService,
    private injector: Injector,
    private logger: NGXLogger,
  ) { }

  // function to update calendar events
  public updateCalendar() {
    this.calendarSubject.next(true);
  }

  // functions to trigger spinner in app component
  // +1 spinner req
  public startSpinnerReq() {
    this.spinnerReqs.next(this.spinnerReqs.value + 1);
  }
  // -1 spinner req
  public stopSpinnerReq() {
    this.spinnerReqs.next(Math.max(0, this.spinnerReqs.value - 1));
  }
  // reset spinner reqs
  public stopSpinner() {
    this.spinnerReqs.next(0);
  }

  // get binary options (yes/no)
  public getBinaryOptions(): {name: string, value: boolean}[] {
    return _.map(binaryOptions, (option) => {
      return {...option, name: this.injector.get(TranslateService).instant(option.name)};
    });
  }

  // get active options
  public getActiveOptions(): {name: string, value: boolean}[] {
    return _.map(activeOptions, (option) => {
      return {...option, name: this.injector.get(TranslateService).instant(option.name)};
    });
  }
  
  // get quotes active options
  public getQuotesActiveOptions(): {name: string, value: boolean}[] {
    return _.map(quoteActiveOptions, (option) => {
      return {...option, name: this.injector.get(TranslateService).instant(option.name)};
    });
  }

  // get customers private options
  public getPrivateOptions(): {name: string, value: boolean}[] {
    return _.map(privateOptions, (option) => {
      return {...option, name: this.injector.get(TranslateService).instant(option.name)};
    });
  }

  // get invoices status options
  public getInvoicesStatusOptions(): {name: string, value: string}[] {
    return _.map(invoiceStatusOptions, (option) => {
      return {...option, name: this.injector.get(TranslateService).instant(`status.${option.name}`)};
    });
  }

  // get invoices types options
  public getInvoicesTypesOptions(): {name: string, value: string}[] {
    return _.map(invoiceTypesOptions, (option) => {
      return {...option, name: `${option.value} - ${this.injector.get(TranslateService).instant(`invoice-types.${option.name}`)}`};
    });
  }

  // get payment methods options
  public getPaymentMethodsOptions(): {name: string, value: string}[] {
    return _.map(paymentMethods, (option): {name: string, value: string} => {
      return {value: option.id, name: this.injector.get(TranslateService).instant(`payment-methods.${option.name}`)};
    });
  }

  // get quantity types options
  public getQuantityTypesOptions(): {name: string, value: string}[] {
    return _.map(quantityTypes, (option): {name: string, value: string} => {
      return {value: option.id, name: this.injector.get(TranslateService).instant(`quantity-types.${option.name}`)};
    });
  }

  // get occupation types options
  public getOccupationTypesOptions(): {name: string, value: string}[] {
    return _.map(occupationTypes, (option): {name: string, value: string} => {
      return {value: option.id, name: this.injector.get(TranslateService).instant(`occupation-types.${option.name}`)};
    });
  }

  // get customer centers options
  public getCustomerCentersOptions(): {name: string, value: string}[] {
    return _.map(customerCenters, (option) => {
      return {...option, name: this.injector.get(TranslateService).instant(`customer-centers.${option.name}`)};
    });
  }

  // get available colors for select
  public getColorOptions(): {id: string, name: string}[] {
    return _.map(colors, (color: string) => {
      return {id: color, name: color};
    });
  }

  // get items mode options
  public getItemsModeOptions(): {name: string, value: string}[] {
    return _.map(itemsModesOptions, (option) => {
      return {...option, name: this.injector.get(TranslateService).instant(`items-modes.${option.name}`)};
    });
  }

  // get invoice mode options
  public getInvoiceModeOptions(): {name: string, value: string}[] {
    return _.map(invoiceModesOptions, (option) => {
      return {...option, name: this.injector.get(TranslateService).instant(`invoice-modes.${option.name}`)};
    });
  }

  // get document with signature types
  public getDocSignatureTypes(): DocSignatureType[] {
    return _.map(docSignatureTypes, (option) => {
      return {...option, name: this.injector.get(TranslateService).instant(`doc-signature-types.${option.name}`)};
    });
  }

  // get appointment attendance options
  public getAttendanceOptions(): {name: string, value: string}[] {
    return _.map(attendanceOptions, (option) => {
      return {...option, name: this.injector.get(TranslateService).instant(`status.${option.name}`)};
    });
  }

  // get timesheet record options
  public getTimesheetRecordOptions(): {name: string, value: string}[] {
    return _.map(timesheetRecordOptions, (option) => {
      return {...option, name: this.injector.get(TranslateService).instant(`timesheet-record-types.${option.name}`)};
    });
  }

  // get timesheet record status options
  public getTimesheetRecordStatusOptions(): {name: string, value: string}[] {
    return _.map(timesheetRecordStatusOptions, (option) => {
      return {...option, name: this.injector.get(TranslateService).instant(`status.${option.name}`)};
    });
  }

  // get absence types options
  public getAbsenceTypes(): {name: string, value: string}[] {
    return _.map(absenceTypes, (option) => {
      return {...option, name: this.injector.get(TranslateService).instant(`absence-types.${option.name}`)};
    });
  }

  // get vacation types options
  public getVacationTypes(): {name: string, value: string}[] {
    return _.map(vacationTypes, (option) => {
      return {...option, name: this.injector.get(TranslateService).instant(`vacation-types.${option.name}`)};
    });
  }

  // function to open drawer
  public openDrawer(component: Type<any>, data?: any): Promise<any> {
    const position: DrawerPosition = data?.position || 'right';
    this.drawerStates[position].next({ isOpen: true, component, data });
    this.drawerCloseSubjects[position] = new Subject<any>();
    return new Promise(resolve => {
      this.drawerCloseSubjects[position].subscribe(response => resolve(response));
    });
  }

  // function to close drawer
  public closeDrawer(position: DrawerPosition = 'right', response?: any): void {
    this.drawerStates[position].next({ isOpen: false, component: null, response });
    this.drawerCloseSubjects[position].next(response);
    this.drawerCloseSubjects[position].complete();
  }

  // function to trigger toast message in app component
  public toast(message: string, type?: string): void {
    this.toastSubject.next({ message, type });
  }

  // confirm modal
  public confirm(message: string): Promise<boolean> {
    return new Promise(resolve => {
      this.confirmationService.confirm({
        message: message,
        acceptIcon: "none",
        rejectIcon: "none",
        key: 'dialog',
        accept: () => {
          resolve(true);
        },
        reject: () => {
          resolve(false);
        }
      });
    });
  }

  // function to transform string hours for timepicker
  public timeTransform(time: string, date?: Date): Date {
    const [hours, minutes, seconds] = time.split(':').map(Number);
    let timeOnlyDate = date ?? new Date();
    if(!_.isDate(timeOnlyDate)) timeOnlyDate = moment(date).toDate();
    timeOnlyDate.setHours(hours, minutes, seconds, 0);
    return timeOnlyDate;
  }

  // function to download blob file
  public downloadFile(blob: Blob, filename: string): void {
    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = filename || `file_${moment().format('YYYY-MM-DD_HH:mm')}`;
    a.click();
    window.URL.revokeObjectURL(downloadUrl);
  }

  // function to download blob file
  public downloadXmlFile(file: any, filename: string): void {
    const blob = new Blob([file], { type: 'application/xml' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || `file_${moment().format('YYYY-MM-DD_HH:mm')}`;
    a.click();
    window.URL.revokeObjectURL(url);
}
  
  // function to convert blob to base64
  public convertBlobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64data = reader.result as string;
        resolve(base64data);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  // generate MD5 from file
  public generateMD5(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        const arrayBuffer = reader.result as ArrayBuffer;
        const wordArray = CryptoJS.lib.WordArray.create(arrayBuffer);
        const hash = CryptoJS.MD5(wordArray).toString();
        resolve(hash);
      };

      reader.onerror = (error) => {
        reject(error);
      };

      reader.readAsArrayBuffer(file);
    });
  }

  // function to generate default avatar with given name initials
  public generateInitialsAvatar(name: string, size: number = 40): string {
    // get initials
    const initials = (_.replace(name, /[-_]/g, '') || 'a a')
      .trim()
      .split(' ')
      .map(word => _.toUpper(word[0]))
      .join('')
      .slice(0, 2);

    // get real values from CSS vars
    const rootStyles = getComputedStyle(document.documentElement);
    const bgColorDefault = rootStyles.getPropertyValue('--main-color-darker').trim() || '#72b6ea';
    const bgColor = this.stringToColor(initials) || bgColorDefault;
    const textColor = rootStyles.getPropertyValue('--white-color').trim() || '#ffffff';
  
    // create canvas
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
  
    const ctx = canvas.getContext('2d');
    if (!ctx) return '';
  
    // print background
    ctx.fillStyle = bgColor;
    ctx.fillRect(0, 0, size, size);
  
    // print centered text
    ctx.fillStyle = textColor;
    ctx.font = `${size / 2}px roboto-regular`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const metrics = ctx.measureText(initials);
    const actualHeight = metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent;
    const yPos = size / 2 + (metrics.actualBoundingBoxAscent - actualHeight / 2);
    ctx.fillText(initials, size / 2, yPos);
  
    // convert to base64
    return canvas.toDataURL('image/png');
  }

  // function to get hex color from name initials string
  private stringToColor(initials: string): string {
    let sum = 0;
    for (let i = 0; i < initials.length; i++) {
      sum += initials.charCodeAt(i);
    }
    return colors[sum % colors.length];
  }

  // function to format number
  public formatNumber(value: number | string): string {
    if (value == null || value === '') return '';
    let num = typeof value === 'string' ? parseFloat(value.replace(',', '.')) : value;
    if (isNaN(num)) return value.toString();

    const parts = num.toFixed(2).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return parts.join(',');
  }

  // function to format every number field in an object
  public formatNumbersInObject(obj: any): any {
    return _.cloneDeepWith(obj, (value: any): any => {
      if (_.isNumber(value) || (_.isString(value) && !isNaN(Number(value.replace(',', '.'))))) {
        return this.formatNumber(value);
      }
    });
  }

  // function to convert formatted number (string) to number
  public toNumber(value: string): number {
    return _.toNumber(_.toString(value).replace(/\./g, '').replace(',', '.'));
  }

  // function to add tippy tooltip to HTML element
  public addTooltip(element: HTMLElement, text: string, color?: string): void {
    const existingTippy = (element as any)._tippy;
    if (existingTippy) existingTippy.destroy();
    tippy(element, {
      content: text,
      allowHTML: true,
      placement: 'top',
      theme: 'light',
      arrow: true,
      onMount(instance) {
        if(color) {
          const box = instance.popper.querySelector('.tippy-box') as HTMLElement;
          const arrow = instance.popper.querySelector('.tippy-arrow') as HTMLElement;
          if (box) {
            box.style.backgroundColor = color;
            box.style.color = 'var(--font-color)';
          }
          if (arrow) {
            arrow.style.color = color;
          }
        }
      },
    });
  }

  // function to remove tippy tooltip from HTML element
  public removeTooltip(element: HTMLElement): void {
    const existingTippy = (element as any)._tippy;
    if (existingTippy) existingTippy.destroy();
  }

  // function to add required at least one field validator to form
  public atLeastOneRequired(controls: string[]) {
    return (group: AbstractControl) => {
      if(!_.some(controls, (control: string) => {
        return group.get(control)?.value;
      })) {
        return { atLeastOneRequired: {fields: _.join(_.map(controls, (control: string) => {
          return this.injector.get(TranslateService).instant(`field.${control}`);
        }), ', ')} };
      }
      return null;
    };
  }

  // function to copy text to clipboard
  public copyToClipboard(txt: any): void {
    navigator.clipboard.writeText(txt).then(() => {
      this.logger.debug(this.injector.get(TranslateService).instant('button.copy-to-clipboard'));
      this.toast(this.injector.get(TranslateService).instant('copied-to-clipboard'), 'info');
    });
  }

  // function to export data
  public export(dataToExport: any[], filename: string): void {
    const worksheet = XLSX.utils.json_to_sheet(dataToExport);

    const headers = {};
    _.forEach(_.keys(_.first(dataToExport)), (key: string) => {
      _.set(headers, key, this.injector.get(TranslateService).instant(`field.${key}`));
    });
    _.forEach(_.keys(headers), (key, index) => {
      const cell = XLSX.utils.encode_cell({ r: 0, c: index });
      worksheet[cell] = {
        t: 's',
        v: _.get(headers, key, ''),
      };
    });

    const workbook = {
      Sheets: { data: worksheet },
      SheetNames: ['data']
    };

    const buffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array'
    });

    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    FileSaver.saveAs(blob, `${filename}.xlsx`);
  }

  // function to make beep sound
  public sound(): void {
    const audio = new Audio('assets/sounds/beep.wav');
    audio.play();
  }

  // function to get all form (fields) errors in array
  public getFormErrors(form: FormGroup): {field: string, errors: any}[] {
    return _.map(_.pickBy(form.controls, control => control.invalid), (control, field) => ({
        field,
        errors: control.errors
      })
    );
  }
}
